import { isNonEmpty } from '@sgme/fp';
import type { Action } from 'state/actions';
import { QuoteState } from 'state/quotes/infos/infos.model';
import { addKey, addOrPatchKey, addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';
import { dateNow } from 'utils/core/now';

import type { EssentialFormInfos, EssentialFormInfosStateMap } from './model';

export function infosReducer(state: EssentialFormInfosStateMap = {}, action: Action): EssentialFormInfosStateMap {
  switch (action.type) {
    case 'DEFAULT_FORM_RECEIVED': {
      if (action.workspace !== 'essential') {
        return state;
      }
      const {
        tabId,
        productView,
        productDescription,
        version,
        negotiationId,
        actions,
        quoteQuery,
        remunerationMode,
        mustForceAdvancedMode,
        validationErrors,
        sendWholeFormData,
        instrumentName,
      } = action;
      return addKey(state, tabId, <EssentialFormInfos>{
        actions,
        negotiationId,
        productDescription,
        instrumentName,
        productView,
        quoteQuery,
        version,
        remunerationMode,
        mustForceAdvancedMode,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        sendWholeFormData,
      });
    }
    case 'FORM_ENRICHMENT_RECEIVED': {
      if (action.workspace !== 'essential') {
        return state;
      }
      const {
        tabId,
        productDescription,
        instrumentName,
        version,
        actions,
        quoteQuery,
        validationErrors,
        remunerationMode,
        mustForceAdvancedMode,
      } = action;
      return patchKey(state, tabId, info => ({
        ...info,
        productDescription,
        version,
        instrumentName,
        actions,
        quoteQuery,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        remunerationMode,
        technicalWarning: undefined,
        mustForceAdvancedMode,
      }));
    }
    case 'FORM_ENRICHMENT_FAILED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({ enrichmentError: action.error }));
    case 'QUOTE_REMOVED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, infos => {
        if (infos.quoteId !== action.quoteId) {
          return infos;
        }
        return {
          quoteId: undefined,
          quoteState: undefined,
        };
      });
    case 'UPDATE_RFQ_VERSION':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        version: action.newVersion,
      }));

    case 'FORM_DOCUMENTATION_V3_RECEIVED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        documentation: {
          docs: action.documentation.docs,
        },
      }));

    case 'QUOTE_ACKED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
        version: action.newVersion,
      }));
    case 'QUOTE_STALE':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteState: action.hasFormChanged ? ('Inconsistent' as QuoteState) : ('Previous' as QuoteState),
      }));
    case 'FORM_RELOADED': {
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrReplaceKey(state, action.tabId, action);
    }
    case 'FORM_TAB_ROLLBACKED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => {
        return {
          enrichmentError: undefined,
          crossBrowserDirty: undefined,
        };
      });
    case 'FORM_ENRICHMENT_REQUESTED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({ enrichmentError: undefined }));
    case 'WORKSPACE_RESET_TAB_REQUESTED':
      return removeKey(state, action.tabId);
    case 'PREVIOUS_QUOTE_LOAD_REQUESTED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Previous' as QuoteState,
      }));
    case 'PREVIOUS_QUOTE_LOADING_FAILED':
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: undefined,
        quoteState: undefined,
      }));
    case 'TRADE_AUTH_RECEIVED': {
      if (action.workspace !== 'essential') {
        return state;
      }
      const tradeAuthCreationDate = action.when.getTime();
      const now = dateNow();
      const isPastQuote = tradeAuthCreationDate + action.expirationDelayInSeconds * 1000 < now;
      const tradeAuthGranted = action.status === 'Granted';

      if (!tradeAuthGranted) {
        return addOrPatchKey(state, action.tabId, () => ({
          quoteId: action.quoteId,
          quoteState: 'Live' as QuoteState,
        }));
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: isPastQuote ? 'Previous' : ('Live' as QuoteState),
      }));
    }
    case 'QUOTE_TRADE_REJECTED': {
      if (action.workspace !== 'essential') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
      }));
    }

    default:
      return state;
  }
}
